import React, { createRef } from 'react';
import { Download, ChevronLeft, ChevronRight, Pencil, Eraser, Save, X } from 'lucide-react';

export interface PhotoFileModel {
    id: string;
    fileName: string;
    createdAt?: string;
    photoSourceURL: string;
}

interface Props {
    photoFiles: PhotoFileModel[];
    initialPhotoIndex?: number;
    onClose: () => void;
    onSaveAnnotation?: (photoId: string, annotatedDataUrl: string) => Promise<void>;
    // New props for image compression
    outputFormat?: 'png' | 'jpeg' | 'webp';
    outputQuality?: number; // 0.1 to 1.0 for JPEG/WebP
    maxOutputSize?: number; // Maximum width/height in pixels
}

interface State {
    currentPhotoIndex: number;
    isDrawing: boolean;
    isAnnotationMode: boolean;
    annotationColor: string;
    brushSize: number;
    canvasWidth: number;
    canvasHeight: number;
    isLoading: boolean;
}

class FullPhotoView extends React.Component<Props, State> {
    canvasRef = createRef<HTMLCanvasElement>();
    imageRef = createRef<HTMLImageElement>();
    lastX: number = 0;
    lastY: number = 0;
    ctx: CanvasRenderingContext2D | null = null;
    originalImageData: ImageData | null = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            currentPhotoIndex: props.initialPhotoIndex || 0,
            isDrawing: false,
            isAnnotationMode: false,
            annotationColor: '#ff0000', // Default red color
            brushSize: 3,
            canvasWidth: 0,
            canvasHeight: 0,
            isLoading: false,
        };

        // Bind methods
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.goToPreviousPhoto = this.goToPreviousPhoto.bind(this);
        this.goToNextPhoto = this.goToNextPhoto.bind(this);
        this.toggleAnnotationMode = this.toggleAnnotationMode.bind(this);
        this.startDrawing = this.startDrawing.bind(this);
        this.draw = this.draw.bind(this);
        this.stopDrawing = this.stopDrawing.bind(this);
        this.handleImageLoad = this.handleImageLoad.bind(this);
        this.clearAnnotations = this.clearAnnotations.bind(this);
        this.saveAnnotation = this.saveAnnotation.bind(this);
        this.changeAnnotationColor = this.changeAnnotationColor.bind(this);
        this.changeBrushSize = this.changeBrushSize.bind(this);
    }

    componentDidMount() {
        // Add keyboard event listener
        document.addEventListener('keydown', this.handleKeyDown);
        // document.getElementById('main-layout-1')?.classList.add('overflow-y-hidden');

        document.body.style.overflow = 'hidden';
    }

    componentWillUnmount() {
        // Clean up event listener
        document.removeEventListener('keydown', this.handleKeyDown);
        // document.getElementById('main-layout-1')?.classList.remove('overflow-y-hidden');
        document.body.style.overflow = 'auto';
    }

    handleKeyDown(event: KeyboardEvent) {
        // Only handle navigation if not in annotation mode (to prevent conflicts with drawing)
        if (!this.state.isAnnotationMode) {
            switch (event.key) {
                case 'ArrowLeft':
                    this.goToPreviousPhoto();
                    break;
                case 'ArrowRight':
                    this.goToNextPhoto();
                    break;
                case 'Escape':
                    this.props.onClose();
                    break;
                default:
                    break;
            }
        } else if (event.key === 'Escape') {
            // Allow escape key to exit annotation mode
            this.setState({ isAnnotationMode: false });
        }
    }

    handleImageLoad() {
        if (this.imageRef.current && this.canvasRef.current) {
            const img = this.imageRef.current;
            const canvas = this.canvasRef.current;

            // Set canvas dimensions to match the displayed image
            const displayedWidth = img.clientWidth;
            const displayedHeight = img.clientHeight;

            this.setState(
                {
                    canvasWidth: displayedWidth,
                    canvasHeight: displayedHeight,
                },
                () => {
                    // After state update, initialize canvas context
                    if (canvas) {
                        this.ctx = canvas.getContext('2d');
                        if (this.ctx) {
                            this.ctx.lineCap = 'round';
                            this.ctx.lineJoin = 'round';
                        }
                    }
                },
            );
        }
    }

    toggleAnnotationMode() {
        // Ensure the image is loaded before toggling
        this.setState(
            (prevState) => ({
                isAnnotationMode: !prevState.isAnnotationMode,
            }),
            () => {
                if (this.state.isAnnotationMode && this.canvasRef.current) {
                    this.handleImageLoad();
                }
            },
        );
    }

    startDrawing(e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) {
        if (!this.state.isAnnotationMode || !this.ctx) return;

        // Store original canvas state for the first time
        if (!this.originalImageData && this.canvasRef.current) {
            this.originalImageData = this.ctx.getImageData(
                0,
                0,
                this.canvasRef.current.width,
                this.canvasRef.current.height,
            );
        }

        // Get position based on event type
        let position;
        if ('touches' in e) {
            // Touch event
            const rect = this.canvasRef.current?.getBoundingClientRect();
            if (rect) {
                position = {
                    x: e.touches[0].clientX - rect.left,
                    y: e.touches[0].clientY - rect.top,
                };
            }
        } else {
            // Mouse event
            const rect = this.canvasRef.current?.getBoundingClientRect();
            if (rect) {
                position = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                };
            }
        }

        if (position) {
            this.setState({ isDrawing: true });
            this.lastX = position.x;
            this.lastY = position.y;
        }
    }

    draw(e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) {
        if (!this.state.isDrawing || !this.state.isAnnotationMode || !this.ctx) return;
        e.preventDefault(); // Prevent scrolling on touch devices

        // Get position based on event type
        let position;
        if ('touches' in e) {
            // Touch event
            const rect = this.canvasRef.current?.getBoundingClientRect();
            if (rect) {
                position = {
                    x: e.touches[0].clientX - rect.left,
                    y: e.touches[0].clientY - rect.top,
                };
            }
        } else {
            // Mouse event
            const rect = this.canvasRef.current?.getBoundingClientRect();
            if (rect) {
                position = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                };
            }
        }

        if (position) {
            const { annotationColor, brushSize } = this.state;
            this.ctx.strokeStyle = annotationColor;
            this.ctx.lineWidth = brushSize;

            this.ctx.beginPath();
            this.ctx.moveTo(this.lastX, this.lastY);
            this.ctx.lineTo(position.x, position.y);
            this.ctx.stroke();

            this.lastX = position.x;
            this.lastY = position.y;
        }
    }

    stopDrawing() {
        this.setState({ isDrawing: false });
    }

    clearAnnotations() {
        if (this.ctx && this.canvasRef.current && this.originalImageData) {
            // Restore the original image data (blank canvas)
            this.ctx.putImageData(this.originalImageData, 0, 0);
        } else if (this.ctx && this.canvasRef.current) {
            // If no saved data, just clear the canvas
            this.ctx.clearRect(0, 0, this.canvasRef.current.width, this.canvasRef.current.height);
        }
    }
    saveAnnotation() {
        this.setState({ isLoading: true });

        if (!this.canvasRef.current || !this.props.onSaveAnnotation) {
            this.setState({ isLoading: false });

            return;
        }

        const currentPhoto = this.props.photoFiles[this.state.currentPhotoIndex];
        // Get compression settings with sensible defaults
        const outputFormat = this.getOptimalOutputFormat(currentPhoto.photoSourceURL);
        const outputQuality = this.props.outputQuality || (outputFormat === 'jpeg' ? 0.85 : 0.8);
        const maxOutputSize = this.props.maxOutputSize || 2048; // Max 2048px on any side

        // Create a new canvas to combine the image and annotations
        const combinedCanvas = document.createElement('canvas');
        const combinedCtx = combinedCanvas.getContext('2d');

        if (!combinedCtx) {
            console.error('Could not create canvas context for combining images');
            this.setState({ isLoading: false });
            return;
        }

        // Create a new image element to load the original photo
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Handle CORS if needed

        // Set up the onload handler before setting the src
        img.onload = () => {
            // Calculate the output dimensions with size constraints
            let outputWidth = img.naturalWidth;
            let outputHeight = img.naturalHeight;

            // Apply size constraints if maxOutputSize is set
            if (maxOutputSize && (outputWidth > maxOutputSize || outputHeight > maxOutputSize)) {
                const aspectRatio = outputWidth / outputHeight;
                if (outputWidth > outputHeight) {
                    outputWidth = maxOutputSize;
                    outputHeight = Math.round(maxOutputSize / aspectRatio);
                } else {
                    outputHeight = maxOutputSize;
                    outputWidth = Math.round(maxOutputSize * aspectRatio);
                }
            }

            // Set combined canvas dimensions
            combinedCanvas.width = outputWidth;
            combinedCanvas.height = outputHeight;

            // Draw the original image first (scaled to output size)
            combinedCtx.drawImage(img, 0, 0, outputWidth, outputHeight);

            // Calculate scaling factors for annotations
            const scaleX = outputWidth / this.state.canvasWidth;
            const scaleY = outputHeight / this.state.canvasHeight;

            // Draw the annotations on top with scaling
            combinedCtx.save();
            combinedCtx.scale(scaleX, scaleY);
            combinedCtx.drawImage(this.canvasRef.current!, 0, 0);
            combinedCtx.restore();

            // Get the final combined image as data URL with proper format and quality
            let combinedDataUrl: string;

            if (outputFormat === 'jpeg') {
                combinedDataUrl = combinedCanvas.toDataURL('image/jpeg', outputQuality);
            } else if (outputFormat === 'webp') {
                combinedDataUrl = combinedCanvas.toDataURL('image/webp', outputQuality);
            } else {
                // PNG format (lossless, no quality parameter)
                combinedDataUrl = combinedCanvas.toDataURL('image/png');
            }

            // Log the size improvement for debugging
            const originalSize = Math.round(combinedCanvas.toDataURL('image/png').length * 0.75); // Approximate original size
            const compressedSize = Math.round(combinedDataUrl.length * 0.75); // Approximate compressed size
            console.log(
                `Image compression: ${outputFormat.toUpperCase()} at ${Math.round(outputQuality * 100)}% quality`,
            );
            console.log(`Size reduction: ~${Math.round((1 - compressedSize / originalSize) * 100)}%`);
            console.log(`Output dimensions: ${outputWidth}x${outputHeight}`);

            // Send the combined image to the callback
            if (this.props.onSaveAnnotation) {
                this.props
                    ?.onSaveAnnotation(currentPhoto.id, combinedDataUrl)
                    .then(() => {
                        // Exit annotation mode after successful save
                        this.setState({ isAnnotationMode: false });
                    })
                    .catch((error) => {
                        console.error('Error saving annotation:', error);
                        // You could add UI feedback here
                    });
            }
        }; // Set error handler
        img.onerror = () => {
            console.error('Error loading the original image for annotation combination');
            // Fallback to just saving the annotations with compression
            const outputFormat = this.getOptimalOutputFormat(currentPhoto.photoSourceURL);
            const outputQuality = this.props.outputQuality || (outputFormat === 'jpeg' ? 0.85 : 0.8);

            let annotationDataUrl: string;
            if (outputFormat === 'jpeg') {
                annotationDataUrl = this.canvasRef.current!.toDataURL('image/jpeg', outputQuality);
            } else if (outputFormat === 'webp') {
                annotationDataUrl = this.canvasRef.current!.toDataURL('image/webp', outputQuality);
            } else {
                annotationDataUrl = this.canvasRef.current!.toDataURL('image/png');
            }

            this.props.onSaveAnnotation!(currentPhoto.id, annotationDataUrl)
                .then(() => this.setState({ isAnnotationMode: false }))
                .catch((error) => console.error('Error saving annotation:', error));
        };

        // Start loading the image
        img.src = currentPhoto.photoSourceURL;
    }

    changeAnnotationColor(color: string) {
        this.setState({ annotationColor: color });
    }
    changeBrushSize(size: number) {
        this.setState({ brushSize: size });
    }

    // Utility method to detect optimal output format based on original image
    getOptimalOutputFormat(imageUrl: string): 'jpeg' | 'png' | 'webp' {
        // If format is explicitly set in props, use that
        if (this.props.outputFormat) {
            return this.props.outputFormat;
        }

        // Try to detect from file extension or URL
        const url = imageUrl.toLowerCase();
        if (url.includes('.png') || url.includes('png')) {
            // For PNG images with potential transparency, keep as PNG unless it's clearly a photo
            return 'png';
        } else if (url.includes('.jpg') || url.includes('.jpeg') || url.includes('jpeg')) {
            // For JPEG images, use JPEG for better compression
            return 'jpeg';
        } else if (url.includes('.webp') || url.includes('webp')) {
            // For WebP images, use WebP if supported
            return 'webp';
        }

        // Default to JPEG for photos (better compression for most repair shop images)
        return 'jpeg';
    }
    goToPreviousPhoto() {
        const { photoFiles } = this.props;
        const { currentPhotoIndex, isAnnotationMode } = this.state;

        // Don't allow navigation when in annotation mode
        if (isAnnotationMode) return;

        if (currentPhotoIndex > 0) {
            this.setState(
                {
                    currentPhotoIndex: currentPhotoIndex - 1,
                },
                () => {
                    // Reset canvas when changing photos
                    if (this.canvasRef.current && this.ctx) {
                        this.ctx.clearRect(0, 0, this.canvasRef.current.width, this.canvasRef.current.height);
                        this.originalImageData = null;
                    }
                },
            );
        } else {
            // Wrap around to last photo
            this.setState(
                {
                    currentPhotoIndex: photoFiles.length - 1,
                },
                () => {
                    // Reset canvas when changing photos
                    if (this.canvasRef.current && this.ctx) {
                        this.ctx.clearRect(0, 0, this.canvasRef.current.width, this.canvasRef.current.height);
                        this.originalImageData = null;
                    }
                },
            );
        }
    }

    goToNextPhoto() {
        const { photoFiles } = this.props;
        const { currentPhotoIndex, isAnnotationMode } = this.state;

        // Don't allow navigation when in annotation mode
        if (isAnnotationMode) return;

        if (currentPhotoIndex < photoFiles.length - 1) {
            this.setState(
                {
                    currentPhotoIndex: currentPhotoIndex + 1,
                },
                () => {
                    // Reset canvas when changing photos
                    if (this.canvasRef.current && this.ctx) {
                        this.ctx.clearRect(0, 0, this.canvasRef.current.width, this.canvasRef.current.height);
                        this.originalImageData = null;
                    }
                },
            );
        } else {
            // Wrap around to first photo
            this.setState(
                {
                    currentPhotoIndex: 0,
                },
                () => {
                    // Reset canvas when changing photos
                    if (this.canvasRef.current && this.ctx) {
                        this.ctx.clearRect(0, 0, this.canvasRef.current.width, this.canvasRef.current.height);
                        this.originalImageData = null;
                    }
                },
            );
        }
    }

    render() {
        const { photoFiles, onClose, onSaveAnnotation } = this.props;
        const {
            currentPhotoIndex,
            isAnnotationMode,
            annotationColor,
            brushSize,
            canvasWidth,
            canvasHeight,
            isLoading,
        } = this.state;

        // Get current photo details
        const currentPhoto = photoFiles[currentPhotoIndex];
        const currentPhotoURL = currentPhoto.photoSourceURL;
        const currentFileName = currentPhoto.fileName;
        const hasMultiplePhotos = photoFiles.length > 1;

        // Available annotation colors
        const colors = [
            '#ff0000', // red
            '#00ff00', // green
            '#0000ff', // blue
            '#ffff00', // yellow
            '#ff00ff', // magenta
            '#00ffff', // cyan
        ];

        // Available brush sizes
        const brushSizes = [1, 3, 5, 8];

        return (
            <div
                className="fixed inset-0 z-[9999] bg-black/75 flex items-center justify-center p-4 md:p-6 backdrop-blur-sm"
                onClick={isAnnotationMode ? undefined : onClose}
            >
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-blue-500"></div>
                    </div>
                )}
                <div
                    className="relative max-w-4xl w-full bg-white dark:bg-gray-800 rounded-lg shadow-2xl overflow-hidden transform transition-all"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                            {currentFileName}
                            {hasMultiplePhotos && !isAnnotationMode && (
                                <span className="ml-2 text-sm text-gray-500">
                                    ({currentPhotoIndex + 1} of {photoFiles.length})
                                </span>
                            )}
                            {isAnnotationMode && (
                                <span className="ml-2 text-sm text-red-500 font-medium">Annotation Mode</span>
                            )}
                        </h3>
                        <div className="flex items-center space-x-2">
                            {onSaveAnnotation && (
                                <button
                                    className={`p-1 rounded-md ${
                                        isAnnotationMode
                                            ? 'bg-red-500 text-white'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
                                    onClick={this.toggleAnnotationMode}
                                    aria-label={isAnnotationMode ? 'Exit annotation mode' : 'Enter annotation mode'}
                                    title={isAnnotationMode ? 'Exit annotation mode' : 'Annotate this image'}
                                >
                                    <Pencil className="h-5 w-5" />
                                </button>
                            )}
                            <button
                                className="p-1 rounded-full text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                onClick={onClose}
                                aria-label="Close"
                            >
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className="relative bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
                        {/* Navigation buttons - only show when not in annotation mode */}
                        {hasMultiplePhotos && !isAnnotationMode && (
                            <>
                                <button
                                    className="absolute left-4 p-2 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        this.goToPreviousPhoto();
                                    }}
                                    aria-label="Previous photo"
                                >
                                    <ChevronLeft className="w-6 h-6" />
                                </button>
                                <button
                                    className="absolute right-4 p-2 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        this.goToNextPhoto();
                                    }}
                                    aria-label="Next photo"
                                >
                                    <ChevronRight className="w-6 h-6" />
                                </button>
                            </>
                        )}

                        {/* The photo */}
                        <img
                            ref={this.imageRef}
                            src={currentPhotoURL}
                            alt={currentFileName}
                            title={currentFileName}
                            className="max-h-[75vh] object-contain"
                            onLoad={this.handleImageLoad}
                            style={{ display: isAnnotationMode ? 'block' : 'block' }}
                        />

                        {/* Canvas overlay for annotations */}
                        {isAnnotationMode && (
                            <canvas
                                ref={this.canvasRef}
                                width={canvasWidth}
                                height={canvasHeight}
                                className="absolute top-0 left-0 right-0 mx-auto cursor-crosshair"
                                onMouseDown={this.startDrawing}
                                onMouseMove={this.draw}
                                onMouseUp={this.stopDrawing}
                                onMouseOut={this.stopDrawing}
                                onTouchStart={this.startDrawing}
                                onTouchMove={this.draw}
                                onTouchEnd={this.stopDrawing}
                            />
                        )}
                    </div>

                    {/* Annotation Toolbar */}
                    {isAnnotationMode && (
                        <div className="p-2 bg-gray-200 dark:bg-gray-700 flex flex-wrap gap-2 items-center">
                            {/* Color selection */}
                            <div className="flex items-center space-x-1 mr-4">
                                <span className="text-sm text-gray-700 dark:text-gray-300">Color:</span>
                                {colors.map((color) => (
                                    <button
                                        key={color}
                                        className={`w-6 h-6 rounded-full border-2 ${
                                            annotationColor === color
                                                ? 'border-black dark:border-white'
                                                : 'border-transparent'
                                        }`}
                                        style={{ backgroundColor: color }}
                                        onClick={() => this.changeAnnotationColor(color)}
                                        aria-label={`Select ${color} color`}
                                    />
                                ))}
                            </div>

                            {/* Brush size selection */}
                            <div className="flex items-center space-x-1 mr-4">
                                <span className="text-sm text-gray-700 dark:text-gray-300">Size:</span>
                                {brushSizes.map((size) => (
                                    <button
                                        key={size}
                                        className={`flex items-center justify-center w-7 h-7 rounded ${
                                            brushSize === size
                                                ? 'bg-gray-400 dark:bg-gray-600'
                                                : 'bg-gray-300 dark:bg-gray-800'
                                        }`}
                                        onClick={() => this.changeBrushSize(size)}
                                        aria-label={`Select brush size ${size}`}
                                    >
                                        <div
                                            className="rounded-full bg-current"
                                            style={{ width: size, height: size }}
                                        ></div>
                                    </button>
                                ))}
                            </div>

                            {/* Clear button */}
                            <button
                                className="px-3 py-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 rounded text-sm flex items-center"
                                onClick={this.clearAnnotations}
                            >
                                <Eraser className="w-4 h-4 mr-1" />
                                Clear
                            </button>
                            {/* Cancel button */}
                            <div className="ml-auto flex space-x-2">
                                <button
                                    className="px-4 py-2 flex border border-gray-300 dark:border-gray-600 dark:text-gray-100 text-gray-700 font-medium rounded-md hover:bg-gray-100"
                                    onClick={() => this.setState({ isAnnotationMode: false })}
                                >
                                    Cancel
                                </button>

                                {/* Save button */}
                                {onSaveAnnotation && (
                                    <button
                                        className="px-4 py-2 flex bg-green-600 text-white font-medium rounded-md hover:bg-green-700"
                                        onClick={this.saveAnnotation}
                                    >
                                        Save
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {!isAnnotationMode && (
                        <div className="p-4 bg-gray-50 dark:bg-gray-800 flex justify-between items-center">
                            {hasMultiplePhotos && (
                                <div className="flex space-x-1">
                                    {photoFiles.map((_, index: number) => (
                                        <button
                                            key={index}
                                            className={`w-2.5 h-2.5 rounded-full transition-colors ${
                                                index === currentPhotoIndex
                                                    ? 'bg-blue-600'
                                                    : 'bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500'
                                            }`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                this.setState({ currentPhotoIndex: index });
                                            }}
                                            aria-label={`Go to photo ${index + 1}`}
                                        />
                                    ))}
                                </div>
                            )}
                            <button
                                className="bg-blue-600 text-white rounded py-2 font-semibold hover:bg-blue-700 hover:text-white px-4 flex items-center ml-auto"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const link = document.createElement('a');
                                    link.href = currentPhotoURL;
                                    link.target = '_blank';
                                    link.rel = 'noopener noreferrer';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                }}
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Download
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default FullPhotoView;
